import * as React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { hasAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  lighten,
  Link,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Eye as EyeIcon,
  Megaphone as MegaphoneIcon,
  PaperPlaneTilt as PaperPlaneTiltIcon,
  Plus as PlusIcon,
  Paperclip as PaperclipIcon,
} from '@phosphor-icons/react/dist/ssr';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { useDialog } from '@/hooks/v2/use-dialog';
import dayjs from 'dayjs';

import type { Announcement } from '@/types/announcement';
import { Announcement_Status, Announcement_Type } from '@/types/announcement';
import { paths } from '@/paths';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { RouterLink } from '@/components/core/v2/link';
import CreateAnnouncementDialog from '../dialogs/CreateAnnouncementDialog';

interface AnnouncementsTableProps {
  rows: Announcement[];
  loading: boolean;
  error: Error | null;
}

export function AnnouncementsTable({ rows, loading, error }: AnnouncementsTableProps): React.JSX.Element {
  const { account, community } = useUser();
  const dialog = useDialog();
  const navigate = useNavigate();
  
  const columns = React.useMemo<MRT_ColumnDef<Announcement>[]>(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        maxSize: 120,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 120,
            width: 120,
            maxWidth: 120,
          },
        }),
        accessorFn: (row) => {
          const status = typeof row?.status === 'number' 
            ? Announcement_Status[row?.status as keyof typeof Announcement_Status] 
            : row?.status;
          return (
            <Chip
              color={
                status === 'Published' ? 'success' :
                status === 'Pending' ? 'warning' :
                status === 'Draft' ? 'default' : 'primary'
              }
              label={toTitleCase(String(status) || '')}
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 120,
        maxSize: 120,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 120,
            width: 120,
            maxWidth: 120,
          },
        }),
        accessorFn: (row) => {
          const type = typeof row?.type === 'number' 
            ? Announcement_Type[row?.type as keyof typeof Announcement_Type] 
            : row?.type;
          return (
            <Chip 
              label={toTitleCase(String(type) || '')} 
              size="small" 
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        size: 250,
        maxSize: 250,
        grow: true,
        accessorFn: (row) => (
          <Typography sx={{ fontWeight: 500 }} variant="body2">
            {row.subject || 'No subject'}
          </Typography>
        ),
      },
      {
        accessorKey: 'message',
        header: 'Message',
        size: 300,
        maxSize: 300,
        grow: true,
        accessorFn: (row) => {
          const message = row.message || '';
          return message.length > 100 ? `${message.slice(0, 100)}...` : message;
        },
      },
      {
        accessorKey: 'files',
        header: 'Attachments',
        size: 100,
        maxSize: 100,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 100,
            width: 100,
            maxWidth: 100,
          },
        }),
        accessorFn: (row) => {
          const files = row.files || [];
          if (files.length === 0) {
            return (
              <Typography color="text.secondary" variant="body2">
                No files
              </Typography>
            );
          }
          return (
            <Box alignItems="center" display="flex" gap={1}>
              <PaperclipIcon size={16} />
              <Typography color="text.primary" variant="body2">
                {files.length} file{files.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        maxSize: 120,
        accessorFn: (row) => (
          <Typography color="text.secondary" variant="body2">
            {dayjs(row.created_at).format('MMM DD, YYYY')}
          </Typography>
        ),
      },
      {
        accessorKey: 'scheduled_at',
        header: 'Scheduled',
        size: 120,
        maxSize: 120,
        accessorFn: (row) => (
          <Typography color="text.secondary" variant="body2">
            {row.scheduled_at ? dayjs(row.scheduled_at).format('MMM DD, YYYY') : '-'}
          </Typography>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable<Announcement>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search announcements...',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={() => {
          navigate(paths.announcements.announcement(row.original.id.toString()));
        }}
        size="small"
      >
        <EyeIcon size={20} />
      </IconButton>
    ),
    muiTableContainerProps: (props) => ({
      ...props,
      sx: {
        minHeight: '500px',
      },
    }),
    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
          </Box>
        </Box>
      );
    },
  });

  return (
    <React.Fragment>
      <Card
        elevation={0}
        sx={{
          mt: 4,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardHeader
         
          title={
            <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
             
              <Box display="flex" gap={2}>
                <RoleGuard
                  requiredAccountRoles={[
                    ACCOUNT_ROLES.ACCOUNT_OWNER,
                    ACCOUNT_ROLES.ACCOUNT_ADMIN,
                    ACCOUNT_ROLES.ACCOUNT_MANAGER,
                  ]}
                  requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
                >
                  <Button
                    onClick={dialog.handleOpen}
                    size="small"
                    startIcon={<PlusIcon size={20} />}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                    variant="contained"
                  >
                    Create Announcement
                  </Button>
                </RoleGuard>
              </Box>
            </Box>
          }
          titleTypographyProps={{ variant: 'h6', sx: { fontWeight: 600 } }}
        />
        <Divider />
        <MaterialReactTable table={table} />
      </Card>
      <CreateAnnouncementDialog onClose={dialog.handleClose} open={dialog.open} />
    </React.Fragment>
  );
}
